/**
 * AdminConsentementsView
 * ───────────────────────
 * Tableau de bord admin — rubrique « Consentements ».
 *
 * Affiche la liste des consentements ayant fait l'objet d'une réponse
 * (acceptés / refusés), avec :
 *   - le détail (signataire, date, mode de recueil, signature)
 *   - le téléchargement PDF individuel du formulaire signé
 *   - le téléchargement PDF collectif de tous les consentements
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  ClipboardCheck, CheckCircle2, XCircle, Download, FileDown,
  RefreshCw, Search, Filter, PenLine, AlertCircle
} from 'lucide-react';
import { consentementService } from '../../services/api';

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
};

const StatutBadge = ({ statut }) => {
  const map = {
    accepte: { label: 'Accepté', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
    refuse:  { label: 'Refusé',  cls: 'bg-red-100 text-red-800 border-red-200',             icon: <XCircle className="w-3.5 h-3.5" /> },
  };
  const c = map[statut] || { label: statut, cls: 'bg-slate-100 text-slate-600 border-slate-200', icon: null };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${c.cls}`}>
      {c.icon}{c.label}
    </span>
  );
};

const telechargerBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 10000);
};

export default function AdminConsentementsView() {
  const [consentements, setConsentements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filtre, setFiltre] = useState('tous'); // tous | accepte | refuse
  const [search, setSearch] = useState('');
  const [downloading, setDownloading] = useState(null); // apprenant_id en cours
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [erreur, setErreur] = useState(null);

  const loadData = useCallback(async (silent = false) => {
    silent ? setRefreshing(true) : setLoading(true);
    try {
      const res = await consentementService.list();
      setConsentements(Array.isArray(res.data) ? res.data : []);
      setErreur(null);
    } catch (e) {
      setErreur("Impossible de charger les consentements.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDownloadIndividuel = async (c) => {
    setDownloading(c.apprenant_id);
    try {
      const res = await consentementService.downloadIndividuel(c.apprenant_id);
      telechargerBlob(res.data, `consentement_${c.nom}_${c.prenom}.pdf`.replace(/\s+/g, '_'));
    } catch {
      alert("Erreur lors du téléchargement du PDF.");
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadCollectif = async () => {
    setDownloadingAll(true);
    try {
      const statut = (filtre === 'accepte' || filtre === 'refuse') ? filtre : null;
      const res = await consentementService.downloadCollectif(statut);
      telechargerBlob(res.data, `consentements_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch {
      alert("Erreur lors du téléchargement collectif.");
    } finally {
      setDownloadingAll(false);
    }
  };

  const term = search.trim().toLowerCase();
  const liste = consentements.filter(c => {
    const matchStatut = filtre === 'tous' || c.statut === filtre;
    const matchTexte = term === '' ||
      `${c.prenom} ${c.nom}`.toLowerCase().includes(term) ||
      (c.zone_geographique || '').toLowerCase().includes(term);
    return matchStatut && matchTexte;
  });

  const nbAcceptes = consentements.filter(c => c.statut === 'accepte').length;
  const nbRefuses = consentements.filter(c => c.statut === 'refuse').length;

  const filtres = [
    { key: 'tous', label: 'Tous', count: consentements.length },
    { key: 'accepte', label: 'Acceptés', count: nbAcceptes },
    { key: 'refuse', label: 'Refusés', count: nbRefuses },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <ClipboardCheck className="w-6 h-6 text-blue-600" />
            Consentements
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Formulaires d'entrée dans le dispositif signés par les bénéficiaires
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => loadData(true)}
            disabled={refreshing}
            variant="outline"
            className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button
            onClick={handleDownloadCollectif}
            disabled={downloadingAll || consentements.length === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
            data-testid="btn-consentements-collectif"
          >
            {downloadingAll
              ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Génération…</>
              : <><FileDown className="w-4 h-4 mr-2" />Télécharger tous (PDF)</>}
          </Button>
        </div>
      </div>

      {erreur && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-100 text-red-800 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />{erreur}
        </div>
      )}

      <Card className="bg-white border-slate-200 rounded-3xl shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-lg font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Liste des consentements
            </CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Filter className="w-4 h-4 text-slate-400 self-center" />
            {filtres.map(f => (
              <button
                key={f.key}
                onClick={() => setFiltre(f.key)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  filtre === f.key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {f.label}
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                  filtre === f.key ? 'bg-blue-500' : 'bg-slate-100 text-slate-500'
                }`}>
                  {f.count}
                </span>
              </button>
            ))}
          </div>
        </CardHeader>

        <CardContent>
          {liste.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Aucun consentement pour ce filtre.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Bénéficiaire</th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Zone</th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Date</th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Recueil</th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {liste.map((c, i) => (
                    <tr key={c.apprenant_id || i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-3">
                        <div className="font-semibold text-slate-900">{c.prenom} {c.nom}</div>
                        {c.commentaire_refus && (
                          <div className="text-xs text-red-500 mt-0.5 truncate max-w-[220px]" title={c.commentaire_refus}>
                            {c.commentaire_refus}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-3 text-slate-500 hidden md:table-cell">{c.zone_geographique || '—'}</td>
                      <td className="py-3 px-3"><StatutBadge statut={c.statut} /></td>
                      <td className="py-3 px-3 text-slate-500 hidden sm:table-cell">{fmt(c.date_reponse)}</td>
                      <td className="py-3 px-3 hidden lg:table-cell">
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                          {c.validation_admin
                            ? <><PenLine className="w-3.5 h-3.5" />Présentiel</>
                            : <>Email</>}
                          {c.a_signature && (
                            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold">
                              signé
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <button
                          onClick={() => handleDownloadIndividuel(c)}
                          disabled={downloading === c.apprenant_id}
                          title="Télécharger le formulaire signé (PDF)"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold
                            bg-blue-50 text-blue-700 border border-blue-200
                            hover:bg-blue-100 hover:border-blue-300 transition-colors
                            disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid={`btn-consentement-pdf-${c.apprenant_id}`}
                        >
                          {downloading === c.apprenant_id
                            ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            : <Download className="w-3.5 h-3.5" />}
                          PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
